import { useEffect } from "react";
import { usePaneStore } from "../state/store-context";
import { rotate90 } from "../canvas/viewport";
import { useBboxToggles } from "../hooks/useBboxToggles";
import { useCycleSnippetZoomPersist } from "../hooks/useCycleSnippetZoomPersist";

interface KeyboardManagerProps {
  /**
   * F-095 — MUST be the validated submit path (`useValidatedAccept` →
   * `runValidatedSubmit` in VerificationStation), never the raw accept POST;
   * every accept gesture below routes through it.
   */
  onAccept: () => void;
  onReject: () => void;
  onHold: () => void;
  onToggleShortcutHelp?: () => void;
  /** R-239 B4 — Ctrl+O: override the active failing field. */
  onOverride?: () => void;
}

export function KeyboardManager({ onAccept, onReject, onHold, onToggleShortcutHelp, onOverride }: KeyboardManagerProps) {
  const advanceField = usePaneStore((s) => s.advanceField);
  // Handwriting Phase 3 — Ctrl+E swap is a pure store action (like
  // advanceField/toggleFocusMode), so it's subscribed directly rather than
  // prop-threaded; it no-ops for fields without candidates (gate-off inert).
  const swapEngineCandidate = usePaneStore((s) => s.swapEngineCandidate);
  const jumpToNextSuspect = usePaneStore((s) => s.jumpToNextSuspect);
  // F-034 — detect the last field so plain Enter there submits instead of
  // advancing (advancing has nowhere to go).
  const fieldCount = usePaneStore((s) => s.fields.length);
  const activeFieldIndex = usePaneStore((s) => s.activeFieldIndex);
  const viewport = usePaneStore((s) => s.viewport);
  const setViewport = usePaneStore((s) => s.setViewport);
  const resetViewport = usePaneStore((s) => s.resetViewport);
  const toggleFocusMode = usePaneStore((s) => s.toggleFocusMode);
  const toggleTerminalMode = usePaneStore((s) => s.toggleTerminalMode);
  const toggleAudio = usePaneStore((s) => s.toggleAudio);
  // E-049 — cycle + persist (was the raw store action; the cycled choice now
  // writes snippetZoomDefault so it survives doc/batch/session transitions).
  const cycleSnippetZoom = useCycleSnippetZoomPersist();
  const pages = usePaneStore((s) => s.pages);
  const setCurrentPage = usePaneStore((s) => s.setCurrentPage);
  const { toggleBboxOverlays, toggleBboxLegend } = useBboxToggles();

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Don't intercept when typing in an input (Tab/Enter handled by field blur flow)
      const target = e.target as HTMLElement;

      // WU-2.3 — supervisor-note reply box (and any future VS-mounted
      // free-text surface) opts out of ALL station keyboard handling:
      // Enter must newline, Tab must stay native, Ctrl+R must not reject
      // a document mid-reply.
      if (target instanceof Element && target.closest("[data-vs-keyboard-exempt]")) return;

      const isInput = target.tagName === "INPUT" || target.tagName === "TEXTAREA";

      // Tab/Enter — advance field (let browser handle the input blur first).
      // Must NOT swallow Ctrl-modified Enter: Ctrl+Enter (accept) and
      // Ctrl+Shift+Enter (accept+next) have to fall through to their handlers
      // below even though focus is normally inside a field input (R-87/R-83 —
      // otherwise accept is permanently shadowed by field-advance).
      if ((e.key === "Tab" || e.key === "Enter") && isInput && !e.ctrlKey) {
        e.preventDefault();
        // F-033 — text/date/number/currency inputs commit + normalize their
        // value to the store only on blur. Advancing from the LAST field doesn't
        // move focus, so without an explicit blur the typed value is never
        // committed and submit validates a stale value. Flush it first.
        target.blur();
        // F-034 — plain Enter (no Shift) on the LAST field submits instead of
        // advancing (Enter elsewhere still advances; Tab always advances/wraps;
        // Shift+Enter goes to the previous field). onAccept routes through the
        // validated submit gate (F-095 — runValidatedSubmit), so an incomplete
        // or failing earlier field jumps the cursor there instead of accepting.
        if (
          e.key === "Enter" &&
          !e.shiftKey &&
          fieldCount > 0 &&
          activeFieldIndex === fieldCount - 1
        ) {
          onAccept();
          return;
        }
        if (e.shiftKey) {
          advanceField("prev");
        } else {
          advanceField("next");
        }
        return;
      }

      // Ctrl+Enter — accept document
      if (e.key === "Enter" && e.ctrlKey && !e.shiftKey) {
        e.preventDefault();
        // F-033 — commit the focused field (blur → store + normalize) before
        // onAccept validates, or accept runs against the stale pre-commit value.
        if (isInput) target.blur();
        onAccept();
        return;
      }

      // Ctrl+Shift+Enter — accept and get next
      if (e.key === "Enter" && e.ctrlKey && e.shiftKey) {
        e.preventDefault();
        if (isInput) target.blur(); // F-033 — flush focused field before validating
        onAccept();
        return;
      }

      // Ctrl+R — reject (prevent browser reload)
      if (e.key === "r" && e.ctrlKey) {
        e.preventDefault();
        onReject();
        return;
      }

      // Ctrl+H — hold
      if (e.key === "h" && e.ctrlKey) {
        e.preventDefault();
        onHold();
        return;
      }

      // Ctrl+; OR F2 — cycle snippet zoom. F2 is dead on default Mac keyboards
      // (OS media key), so Ctrl+; is the primary; it also fires while the cursor
      // is in a field (the operator is keying). R-225.
      if (e.key === "F2" || (e.key === ";" && e.ctrlKey)) {
        e.preventDefault();
        cycleSnippetZoom();
        return;
      }

      // F5 — toggle focus mode (works even in input)
      if (e.key === "F5") {
        e.preventDefault();
        toggleFocusMode();
        return;
      }

      // Ctrl+O — override the active failing field (R-239 B4). Fires even
      // while the cursor is in a field (the operator is on the failing field).
      // Ctrl+O is "open file" in browsers, so preventDefault.
      if (e.key === "o" && e.ctrlKey) {
        e.preventDefault();
        onOverride?.();
        return;
      }

      // Ctrl+E — swap engine candidate on the active field (Handwriting
      // Phase 3). Fires while the cursor is in the field (the keyer is ON the
      // disagreement field); preventDefault (Ctrl+E is address-bar search in
      // some browsers). No-collision check: Enter/R/H/O/B/M/;/1-9 taken.
      if (e.key === "e" && e.ctrlKey) {
        e.preventDefault();
        swapEngineCandidate();
        return;
      }

      // Ctrl+J — jump to the next field with suspect (sub-threshold)
      // charConfidences spans (ADR-013). Pure navigation, wraps; no-op when
      // nothing is suspect. preventDefault (Ctrl+J is downloads in Chrome).
      // No-collision check: E/B/M/R/H/O/;/Enter/1-9 taken.
      if (e.key === "j" && e.ctrlKey) {
        e.preventDefault();
        jumpToNextSuspect();
        return;
      }

      // Ctrl+B — toggle terminal mode
      if (e.key === "b" && e.ctrlKey) {
        e.preventDefault();
        toggleTerminalMode();
        return;
      }

      // Ctrl+M — toggle audio feedback
      if (e.key === "m" && e.ctrlKey) {
        e.preventDefault();
        toggleAudio();
        return;
      }

      // F — toggle focus mode (when not in input)
      if (e.key === "f" && !isInput && !e.ctrlKey) {
        e.preventDefault();
        toggleFocusMode();
        return;
      }

      // R — rotate document (when not in input)
      if (e.key === "r" && !isInput && !e.ctrlKey) {
        e.preventDefault();
        setViewport(rotate90(viewport));
        return;
      }

      // W — fit to width (when not in input)
      if (e.key === "w" && !isInput && !e.ctrlKey) {
        e.preventDefault();
        resetViewport();
        return;
      }

      // B — toggle bbox overlays (when not in input)
      if (e.key === "b" && !isInput && !e.ctrlKey) {
        e.preventDefault();
        toggleBboxOverlays();
        return;
      }

      // L — toggle bbox legend (when not in input)
      if (e.key === "l" && !isInput && !e.ctrlKey) {
        e.preventDefault();
        toggleBboxLegend();
        return;
      }

      // Ctrl+1-9 — jump to page
      if (e.ctrlKey && e.key >= "1" && e.key <= "9") {
        const pageNum = parseInt(e.key) - 1;
        if (pageNum < pages.length) {
          e.preventDefault();
          setCurrentPage(pageNum);
        }
        return;
      }

      // ? — show keyboard shortcut reference (when not in input)
      if (e.key === "?" && !isInput) {
        e.preventDefault();
        onToggleShortcutHelp?.();
        return;
      }

      // +/- zoom (when not in input)
      if (!isInput) {
        if (e.key === "=" || e.key === "+") {
          e.preventDefault();
          setViewport({ scale: Math.min(4, viewport.scale * 1.2) });
          return;
        }
        if (e.key === "-") {
          e.preventDefault();
          setViewport({ scale: Math.max(0.25, viewport.scale / 1.2) });
          return;
        }
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [advanceField, fieldCount, activeFieldIndex, viewport, setViewport, resetViewport, toggleFocusMode, toggleTerminalMode, toggleAudio, cycleSnippetZoom, pages, setCurrentPage, toggleBboxOverlays, toggleBboxLegend, onAccept, onReject, onHold, onToggleShortcutHelp, onOverride, swapEngineCandidate, jumpToNextSuspect]);

  return null; // Invisible component — just a keyboard listener
}
